import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { DiscoverPaywall } from "@/components/discover/DiscoverPaywall";
import { discover } from "@/lib/discover";
import { track, trackPersistentOnce } from "@/lib/analytics";

const TOTAL = 8;
const Progress = ({ n }) => (
  <div className="flex items-center gap-1" data-testid="discover-progress" aria-label={`Question ${n} of ${TOTAL}`}>
    {Array.from({ length: TOTAL }, (_, k) => k + 1).map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${i <= n ? "bg-teal-600" : "bg-slate-200"}`} />)}
  </div>
);

const EllieIntro = () => (
  <div className="mb-6 flex items-start gap-3" data-testid="discover-ellie-intro">
    <EllieOrb size={44} />
    <div className="fio-glass flex-1 space-y-2 rounded-2xl rounded-tl-md px-4 py-3 text-[15px] leading-relaxed text-slate-700">
      <p className="font-semibold text-slate-900">Hi, I'm Ellie! Welcome to FioNova.</p>
      <p>I'm going to ask you eight quick questions to help you explore what's beneath the surface and better understand why you might be feeling the way you do.</p>
      <p>There are no right or wrong answers. Just be yourself.</p>
    </div>
  </div>
);

const Question = ({ q, selected, onPick, busy, intro }) => (
  <motion.div key={q.question} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
    {intro && <EllieIntro />}
    {q.observation && (
      <div className="mb-6 flex items-start gap-3" data-testid="discover-observation">
        <EllieOrb size={40} />
        <p className="fio-glass flex-1 rounded-2xl rounded-tl-md px-4 py-3 text-[15px] leading-relaxed text-slate-700">{q.observation}</p>
      </div>
    )}
    <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-slate-900 sm:text-3xl" data-testid="discover-question">{q.question}</h1>
    <div className="mt-6 space-y-3">
      {q.options.map((o, i) => (
        <button key={o} data-testid={`discover-option-${i}`} disabled={busy} onClick={() => onPick(i)}
          className={`w-full rounded-2xl px-5 py-4 text-left text-[16px] leading-snug ring-1 transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.99] disabled:opacity-60 ${selected === i ? "bg-teal-600 text-white ring-teal-600" : "bg-white text-slate-800 ring-slate-200 hover:ring-teal-400"}`}>
          {o}
        </button>
      ))}
    </div>
  </motion.div>
);

const Thinking = ({ text }) => (
  <div className="flex flex-col items-center py-16 text-center" data-testid="discover-thinking">
    <EllieOrb size={96} />
    <p className="mt-6 max-w-xs text-base text-slate-600">{text}</p>
  </div>
);

// Discover: five adaptive tap questions; the two insights stay on the server until payment is verified.
export default function Discover() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [view, setView] = useState(1);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState("");

  const adopt = (d) => {
    setS(d);
    if (d.status === "paid" || d.status === "claimed") return navigate(`/discover/unlock?d=${d.discover_id}`, { replace: true });
    setView(d.status === "completed" ? TOTAL + 1 : Math.min(d.answers.length + 1, TOTAL));
  };

  useEffect(() => {
    const did = params.get("d") || discover.id();
    (did ? discover.state(did).catch(() => discover.start()) : discover.start()).then((d) => {
      discover.setId(d.discover_id);
      trackPersistentOnce(`discover_started:${d.discover_id}`, "discover_started");
      adopt(d);
    }).catch((e) => setError(e.status === 429 ? "Discover is busy on this network right now. Please try again later." : "Something went wrong. Please refresh."));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (view === TOTAL + 1) track("discover_results_preview_viewed"); }, [view]);

  const pick = async (i) => {
    setBusy(true); setError("");
    try {
      const d = await discover.answer(s.discover_id, view, i);
      track("discover_question_answered", { question_number: view });
      if (view === TOTAL) track("discover_completed");
      setS(d); setView(view + 1);
    } catch (e) {
      setError(e.status === 503 ? "Ellie needs another moment with your answers. Please tap your answer again." : "That didn't go through. Please try again.");
    }
    setBusy(false);
  };

  const unlock = async () => {
    setBusy(true); setError("");
    track("discover_checkout_clicked");
    try {
      const d = await discover.checkout(s.discover_id);
      if (d.unlocked || d.paid) return navigate(`/discover/unlock?d=${s.discover_id}`);
      track("discover_checkout_started");
      window.location.href = d.checkout_url;
    } catch (e) {
      if (e.status === 503) setUnavailable(true); else setError("We couldn't open checkout. Please try again.");
      setBusy(false);
    }
  };
  useEffect(() => { if (view === TOTAL + 1) discover.availability().then((a) => setUnavailable(!a.checkout_ready)); }, [view]);

  const q = s && view <= TOTAL ? (view <= s.answers.length ? s.answers[view - 1] : s.next) : null;
  return (
    <OnboardingLayout testId="page-discover" contentClassName="py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <button data-testid="discover-back" aria-label="Back" onClick={() => (view > 1 && view <= TOTAL + 1 ? setView(view - 1) : navigate("/"))}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-slate-600 ring-1 ring-slate-200"><ArrowLeft className="h-4 w-4" /></button>
          {view <= TOTAL ? <div className="flex-1"><Progress n={view} /></div> : <p className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">Your results</p>}
          {view <= TOTAL && <span className="font-mono text-xs text-slate-500" data-testid="discover-count">{view}/{TOTAL}</span>}
        </div>
        {params.get("cancelled") && view === TOTAL + 1 && <p className="mb-4 text-sm text-slate-600" data-testid="discover-cancelled">No charge was made. Your answers are saved here whenever you're ready.</p>}
        {error && <p className="mb-4 text-sm text-rose-600" data-testid="discover-error">{error}</p>}
        {!s ? (!error && <Thinking text="Ellie is getting ready…" />)
          : busy && view <= TOTAL ? <Thinking text={view === TOTAL ? "Give me a moment. I'm connecting the dots between your answers…" : "Ellie is listening…"} />
          : view <= TOTAL ? <AnimatePresence mode="wait"><Question q={q} selected={view <= s.answers.length ? s.answers[view - 1].choice_index : null} onPick={pick} busy={busy} intro={view === 1} /></AnimatePresence>
          : <DiscoverPaywall preview={s.preview} onUnlock={unlock} busy={busy} unavailable={unavailable} error={error && ""} />}
        <p className="mt-10 text-center text-xs text-slate-400">FioNova supports reflection and personal growth — it isn't therapy or a diagnosis. In crisis? Call or text 988.</p>
      </div>
    </OnboardingLayout>
  );
}
